import { Dispatch, MutableRefObject, SetStateAction } from 'react'
import { useTranslation } from 'react-i18next'
import { useMapEvents } from 'react-leaflet'
import { useParams } from 'react-router-dom'
import { notification } from 'antd'
import useDirectory from 'hooks/useDirectory'
import useMutation from 'hooks/useMutation'
import { LatLngTuple } from 'leaflet'
import { useRecoilState, useRecoilValue } from 'recoil'
import {
  buildingFilesState,
  buildingModeState,
  floorDetailState,
  listChangedPointsState,
  listPointState,
  revalidateFileListState,
  selectedAreaState,
  selectedPointDetail
} from 'store/buildingStore'
import useSWR from 'swr'
import Utils from 'utils'
import Krpano from 'utils/krpano'
import { KrpanoEngine } from 'utils/KrpanoEngine'

import { rotatedMarkerIconDense } from '../CustomMarker'

type Props = {
  imgBounds: LatLngTuple
  leafletRef: MutableRefObject<any>
}

export default function useMap({ imgBounds, leafletRef }: Props) {
  const { id: buildingId, floorId } = useParams()
  const { t } = useTranslation()
  const { navigate } = useDirectory()

  const listChangedPoints = useRecoilValue(listChangedPointsState)
  const selectedArea = useRecoilValue(selectedAreaState)
  const buildingMode = useRecoilValue(buildingModeState)
  const pointDetail = useRecoilValue(selectedPointDetail)
  const [, setFloorDetail] = useRecoilState(floorDetailState)
  const [points, setPoints] = useRecoilState(listPointState)
  const [, setBuildingFiles] = useRecoilState(buildingFilesState)
  const [, setRevalidateFileList] = useRecoilState(revalidateFileListState)

  const { mutate: refetchFloor } = useSWR(
    {
      url: `v1/floors/${floorId}`
    },
    {
      revalidateOnMount: false
    }
  )

  const { mutate: refetchPoints } = useSWR(
    {
      url: 'v1/points',
      params: {
        buildingId,
        floorId
      }
    },
    {
      revalidateOnMount: false
    }
  )

  const { trigger: handlePoint } = useMutation('v1/points')

  const initMap = (
    setImgBounds: Dispatch<SetStateAction<LatLngTuple>>,
    img: any,
    isUnregisteredMap: boolean
  ) => {
    const height = isUnregisteredMap ? window.innerHeight : img.naturalHeight
    const width = isUnregisteredMap ? window.innerWidth : img.naturalWidth
    setImgBounds([height, width])
    // calculate and set MinZoom + MaxZoom level
    const initBound = [
      [0, 0],
      [height, width]
    ]
    leafletRef.current.mapContainerRef?.setMinZoom(-999)
    leafletRef.current.mapContainerRef?.fitBounds(initBound, { animate: false })
    leafletRef.current.mapContainerRef?.setMaxBounds([
      [-500, -1500],
      [height + 500, width + 1500]
    ])
    const minZoomLevel =
      leafletRef.current.mapContainerRef?.getBoundsZoom(initBound)
    leafletRef.current.mapContainerRef?.setView(
      [height / 2, width / 2],
      minZoomLevel,
      {
        animate: false
      }
    )
    leafletRef.current.mapContainerRef?.setMinZoom(minZoomLevel)
    leafletRef.current.mapContainerRef?.setMaxZoom(minZoomLevel + 5)
  }

  const mapOnReady = (
    imagePath: string,
    setImgBounds: Dispatch<SetStateAction<LatLngTuple>>,
    setImageLoading: Dispatch<SetStateAction<boolean | undefined>>,
    isUnregisteredMap: boolean
  ) => {
    Utils.getMeta(imagePath)
      .then((img) => {
        initMap(setImgBounds, img, isUnregisteredMap)
        setImageLoading(false)
      })
      .catch(() => {
        refetchFloor().then((floor: any) => {
          setFloorDetail(floor)
          Utils.getMeta(floor?.mapFile.s3Path)
            .then((img) => {
              initMap(setImgBounds, img, isUnregisteredMap)
              setImageLoading(false)
            })
            .catch(() => {
              navigate('/buildings')
            })
        })
      })
  }

  const handleAddPoint = (
    x: number,
    y: number,
    imgBounds: any,
    image360?: number
  ) => {
    // only addable when user finish sync drag point
    if (
      !leafletRef.current.isAddingPoint &&
      !listChangedPoints.length &&
      buildingId &&
      floorId
    ) {
      // prevent people add point outside of map imgbound latlng
      if (x >= imgBounds[1] || y >= imgBounds[0] || x < 0 || y < 0) {
        return
      }
      leafletRef.current.isAddingPoint = true

      handlePoint(
        {
          data: {
            x: +x.toFixed(3),
            y: +y.toFixed(3),
            building_id: +buildingId,
            area_id: selectedArea,
            floor_id: +floorId,
            order: points.length + 1,
            image360
          }
        },
        {
          onSuccess: (data: any) => {
            setPoints((prev) => [...prev, data.data.point])
            image360 &&
              setBuildingFiles((prev) => ({
                ...prev,
                files: prev.files.map((file) =>
                  file.id === image360
                    ? { ...file, pointId: data.data.point.id }
                    : file
                )
              }))
            leafletRef.current.isAddingPoint = false
          },
          onError: (err) => {
            if (err.response.data.error.key === 'not_change_image_360') {
              refetchPoints()
              setRevalidateFileList(1)
              leafletRef.current.isAddingPoint = false
              notification.error({
                message: t('translation.imageIsUsed')
              })
            }
            Utils.handleErrorNavigate(err, navigate)
          }
        }
      )
    }
  }

  function MapEventHandler() {
    useMapEvents({
      mousemove(e: any) {
        if (
          buildingMode.selectedMode === 'link' &&
          leafletRef.current.initPositionRef &&
          leafletRef.current.createPolylineRef
        ) {
          leafletRef.current.createPolylineRef.setLatLngs([
            leafletRef.current.initPositionRef.latlng,
            e.latlng
          ])
        }
        if (
          leafletRef.current.radarDragFlag.isRadarMode &&
          leafletRef.current.radarDragFlag.isDragging &&
          pointDetail
        ) {
          const newHlookat = Krpano.GetAngle(
            e.latlng.lat - pointDetail.y,
            e.latlng.lng - pointDetail.x
          )
          leafletRef.current.radarDragFlag.rotate = newHlookat
          leafletRef.current.povMarkerRef.setIcon(
            rotatedMarkerIconDense(newHlookat)
          )
        }
      },
      mouseup() {
        if (leafletRef.current.radarDragFlag.isDragging && pointDetail) {
          leafletRef.current.radarDragFlag.isDragging = false
          const hlookat = Krpano.NormalizeHGap(
            new KrpanoEngine('preview').instance?.get('view.hlookat')
          )
          leafletRef.current.radarDragFlag.hgap = Krpano.NormalizeHGap(
            leafletRef.current.radarDragFlag.rotate - hlookat
          )
        }
      },
      click(e: any) {
        buildingMode.selectedMode === 'point' &&
          handleAddPoint(e.latlng.lng, e.latlng.lat, imgBounds)
        if (leafletRef.current.createPolylineRef) {
          leafletRef.current.initPositionRef = null
        }
      }
    })
    return null
  }

  return { MapEventHandler, mapOnReady, handleAddPoint }
}
